// START IMPORTS
import { CookiesStorage } from '@ltvco/refresh-lib';
import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import { useReactivation } from '@ltvco/refresh-lib/payments';
import {
  invalidateSession,
  isUserLoggedIn,
  useSession,
} from '@ltvco/refresh-lib/v1';
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { INITIAL_SEARCH_KEY } from 'components/ChatBV/constants';
import { goToLogin } from 'utils/goToLogin';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface AuthenticatedRouteProps {
  children: JSX.Element;
}
// END INTERFACES_AND_TYPES

export function AuthenticatedRoute({ children }: AuthenticatedRouteProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession(); // should be replaced by the user_info object
  const queryClient = useQueryClient();
  const { isInInternationalOrVinVariations, vinVariation } =
    useSubscriptionAccessConfigContext();
  const location = useLocation();
  const navigate = useNavigate();
  useReactivation({ currentPathName: location.pathname });
  const isOnChatBVFlowVariation = useFeatureIsOn('prop-3502');
  const isOnChatBVFlowFooterVariation = useFeatureIsOn('prop-3640');
  const isInAnyChatBVFlowVariation =
    isOnChatBVFlowVariation || isOnChatBVFlowFooterVariation;
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const isNonCompliant = account?.account.user_info.noncompliant;
  const cookieStorage = new CookiesStorage();
  const complianceRoutes = [
    '/compliance-form',
    '/noncompliance',
    '/compliance-prohibited',
    '/compliance-permitted',
    '/compliance-recomply',
  ];
  const isDashboard = /^\/dashboard\/?$/.test(location.pathname);
  const subscriptionPlanUniqueName =
    account?.account?.subscription_info?.subscription_plan_unique_name ?? '';
  const isExcludedFromChatBV = /international|spanish|portuguese/i.test(
    subscriptionPlanUniqueName
  );

  function handleFreshnessHubUsers() {
    if (!account) return;
    if (account?.account?.staff_info?.staff) return;
    if (
      account?.account.user_info?.design_name === 'freshness' ||
      account?.account.user_info?.design_version === 'v2'
    ) {
      window.location.href = 'https://www.beenverified.com/app/';
    }
  }

  function handleInternationalUsers() {
    if (isDashboard) {
      if (isInInternationalOrVinVariations) {
        vinVariation
          ? navigate('/dashboard/reports')
          : navigate('/dashboard/language');
      }
    }
  }

  function handleChatBVFlowUsers() {
    if (!isInAnyChatBVFlowVariation) {
      const hasChatBVInitialSearch = localStorage.getItem(INITIAL_SEARCH_KEY);
      if (hasChatBVInitialSearch) {
        localStorage.removeItem(INITIAL_SEARCH_KEY);
      }
      return;
    }

    if (isDashboard && !isExcludedFromChatBV) {
      navigate('/chatbv/chat');
    }
  }
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    handleFreshnessHubUsers();
  }, [account]);

  useEffect(() => {
    handleInternationalUsers();
  }, [isInInternationalOrVinVariations, isDashboard]);

  useEffect(() => {
    if (!isUserLoggedIn()) return;
    if (isNonCompliant && !complianceRoutes.includes(location.pathname)) return;

    handleChatBVFlowUsers();
  }, [
    isInAnyChatBVFlowVariation,
    isDashboard,
    isExcludedFromChatBV,
    isNonCompliant,
    location.pathname,
  ]);

  useEffect(() => {
    if (!account?.account || complianceRoutes.includes(location.pathname))
      return;
    if (
      import.meta.env.VITE_TARGET_ENV &&
      !['production', 'staging'].includes(import.meta.env.VITE_TARGET_ENV)
    )
      return;
  }, [account?.account]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (!isUserLoggedIn()) {
    const url = location.pathname + location.search + location.hash;

    if (cookieStorage.getValue('loggedin')) {
      invalidateSession();
      queryClient.clear();

      setTimeout(() => {
        window.location.reload();
      }, 500);
    }

    if (
      import.meta.env.VITE_TARGET_ENV &&
      !['production'].includes(import.meta.env.VITE_TARGET_ENV)
    ) {
      return <Navigate to="/login" state={{ next: url }} replace />;
    }
    goToLogin('https://www.beenverified.com');
    return null;
  }
  if (isNonCompliant && !complianceRoutes.includes(location.pathname)) {
    return (
      <Navigate to={`/noncompliance`} state={{ from: location }} replace />
    );
  }
  if (isExcludedFromChatBV && location.pathname.includes('/chatbv')) {
    return <Navigate to="/dashboard" replace />;
  }
  // END EARLY_RETURNS

  // START RENDER
  return children;
  // END RENDER
}
